import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileDataViewer = ({ user, userProfile, refreshTrigger, onEvent }) => {
  const [liveProfile, setLiveProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user?.id) {
      fetchLiveProfile();
      setupRealtimeSubscription();
    }

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, [user, refreshTrigger]);

  const fetchLiveProfile = async () => {
    setLoading(true);
    onEvent?.('Fetching live profile data', 'info');
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (error) {
        onEvent?.(`Profile fetch error: ${error?.message}`, 'error', error);
        console.error('Profile fetch error:', error);
      } else {
        setLiveProfile(data);
        setLastFetch(new Date());
        onEvent?.('Profile data fetched successfully', 'success', data);
      }
    } catch (error) {
      onEvent?.(`Profile fetch exception: ${error?.message}`, 'error', error);
      console.error('Profile fetch exception:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase?.channel(`profile-changes-${user?.id}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `id=eq.${user?.id}`
        },
        (payload) => {
          onEvent?.('Real-time update received', 'realtime', payload);
          setLiveProfile(payload?.new);
          setLastFetch(new Date());
        }
      )?.subscribe();

    setSubscription(channel);
    onEvent?.('Real-time subscription established', 'info');
  };

  const renderValue = (key, value) => {
    if (value === null) {
      return <span className="text-gray-500 italic">null</span>;
    }
    if (value === undefined) {
      return <span className="text-gray-500 italic">undefined</span>;
    }
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-400' : 'text-red-400'}>{String(value)}</span>;
    }
    if (typeof value === 'string') {
      return <span className="text-yellow-300">"{value}"</span>;
    }
    if (typeof value === 'number') {
      return <span className="text-blue-400">{value}</span>;
    }
    return <span className="text-gray-300">{JSON.stringify(value)}</span>;
  };

  const getFieldType = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const profileData = liveProfile || userProfile;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Live Profile Data</h2>
          <p className="text-sm text-gray-400 mt-1">
            Current user_profiles record from Supabase
          </p>
        </div>
        <div className="flex items-center gap-4">
          {lastFetch && (
            <div className="text-sm text-gray-400">
              Last updated: {lastFetch?.toLocaleTimeString()}
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-sm text-green-400">Real-time Active</span>
          </div>
        </div>
      </div>
      {/* Profile Data Display */}
      {loading ? (
        <div className="flex items-center justify-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        </div>
      ) : profileData ? (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="bg-gray-900 px-4 py-2 border-b border-gray-700 flex items-center justify-between">
            <span className="text-sm font-mono text-gray-400">user_profiles</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchLiveProfile}
              className="text-gray-400 hover:text-white"
            >
              <AppIcon name="RefreshCw" className="w-3 h-3" />
            </Button>
          </div>
          <div className="p-4 font-mono text-sm overflow-x-auto">
            <div className="space-y-2">
              {Object.entries(profileData)?.map(([key, value]) => (
                <div key={key} className="flex items-start gap-4 py-2 border-b border-gray-700/50 last:border-0">
                  <div className="w-1/3 flex-shrink-0">
                    <div className="text-cyan-400 font-medium">{key}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {getFieldType(value)}
                    </div>
                  </div>
                  <div className="flex-1 break-all">
                    {renderValue(key, value)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-8 text-center">
          <AppIcon name="AlertCircle" className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">No profile data available</p>
        </div>
      )}
      {/* JSON Export */}
      {profileData && (
        <details className="bg-gray-800 rounded-lg border border-gray-700">
          <summary className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 transition-colors text-gray-300">
            <span className="font-medium">View Raw JSON</span>
          </summary>
          <div className="p-4 border-t border-gray-700">
            <pre className="text-xs text-gray-300 overflow-x-auto bg-gray-900 p-4 rounded">
              {JSON.stringify(profileData, null, 2)}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
};

export default ProfileDataViewer;